import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { format, addDays } from "date-fns";
import { ro } from "date-fns/locale";
import { 
  CalendarDays, 
  Clock, 
  User, 
  Phone, 
  Mail, 
  MessageSquare,
  CheckCircle2,
  Car,
  Loader2,
  AlertCircle,
  XCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookingPage() {
  const [categories, setCategories] = useState([]);
  const [bookingSettings, setBookingSettings] = useState(null);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsInfo, setSlotsInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    license_category: "",
    preferred_time: "",
    message: ""
  });

  // Fetch categories and booking settings on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, settingsRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/bookings/settings`)
        ]);
        setCategories(catRes.data);
        setBookingSettings(settingsRes.data);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, []);

  // Fetch available slots when date changes
  useEffect(() => {
    if (selectedDate) {
      const fetchSlots = async () => {
        setSlotsLoading(true);
        setFormData(prev => ({ ...prev, preferred_time: "" }));
        try {
          const dateStr = format(selectedDate, "yyyy-MM-dd");
          const res = await axios.get(`${API}/bookings/available-slots/${dateStr}`);
          setAvailableSlots(res.data.available_slots || []);
          setSlotsInfo(res.data);
        } catch (err) {
          console.error("Error fetching slots:", err);
          setAvailableSlots([]);
          setSlotsInfo(null);
        } finally {
          setSlotsLoading(false);
        }
      };
      fetchSlots();
    }
  }, [selectedDate]);

  // Check if a day is disabled
  const isDayDisabled = (date) => {
    if (date < new Date()) return true;
    if (!bookingSettings) return false;
    
    const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const dayName = dayNames[date.getDay()];
    return !bookingSettings.available_days[dayName];
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate) {
      toast.error("Te rugăm să selectezi o dată");
      return;
    }
    
    if (!formData.preferred_time) {
      toast.error("Te rugăm să selectezi o oră");
      return;
    }
    
    if (!formData.name || !formData.email || !formData.phone || !formData.license_category) {
      toast.error("Te rugăm să completezi toate câmpurile obligatorii");
      return;
    }

    setLoading(true);
    
    try {
      const bookingData = {
        ...formData,
        preferred_date: format(selectedDate, "yyyy-MM-dd")
      };
      
      await axios.post(`${API}/bookings`, bookingData);
      setSubmitted(true);
      toast.success("Programarea a fost trimisă cu succes!");
      
      // Open WhatsApp with pre-filled message
      const whatsappNumber = "40740245376";
      const dateFormatted = format(selectedDate, "dd.MM.yyyy");
      const message = `Bună ziua! Vreau să mă programez la școala AUTO CORA.

📋 *Detalii programare:*
👤 Nume: ${formData.name}
📧 Email: ${formData.email}
📞 Telefon: ${formData.phone}
🚗 Categoria: ${formData.license_category}
📅 Data dorită: ${dateFormatted}
🕐 Ora dorită: ${formData.preferred_time}
${formData.message ? `💬 Mesaj: ${formData.message}` : ''}

Mulțumesc!`;
      
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      
    } catch (err) {
      console.error("Error submitting booking:", err);
      const errorMsg = err.response?.data?.detail || "A apărut o eroare. Te rugăm să încerci din nou.";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-20" data-testid="booking-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md mx-auto px-4 text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Archivo'] mb-4">
            Programare Trimisă!
          </h1>
          <p className="text-slate-600 mb-6">
            Mulțumim pentru interesul tău! Te vom contacta în curând pentru confirmarea programării.
          </p>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 mb-8">
            <div className="space-y-3 text-left">
              <div className="flex justify-between">
                <span className="text-slate-500">Nume:</span>
                <span className="font-medium text-slate-900">{formData.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Categorie:</span>
                <span className="font-medium text-slate-900">{formData.license_category}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Data:</span>
                <span className="font-medium text-slate-900">
                  {selectedDate ? format(selectedDate, "dd MMMM yyyy", { locale: ro }) : "-"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Ora:</span>
                <span className="font-medium text-slate-900">{formData.preferred_time}</span>
              </div>
            </div>
          </div>
          <Button 
            data-testid="new-booking-btn"
            onClick={() => {
              setSubmitted(false);
              setFormData({
                name: "",
                email: "",
                phone: "",
                license_category: "",
                preferred_time: "",
                message: ""
              });
              setSelectedDate(undefined);
              setAvailableSlots([]);
              setSlotsInfo(null);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
          >
            Programare Nouă
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="booking-page">
      {/* Hero Section */}
      <section className="bg-white py-16 md:py-20 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
              Programări Online
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 mt-4 tracking-tight font-['Archivo']">
              Rezervă-ți Locul
            </h1>
            <p className="text-slate-600 mt-6 text-lg">
              Selectează data și ora dorită. Locurile disponibile se actualizează în timp real.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Calendar & Slots */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-2 space-y-6"
            >
              <Card className="border border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-slate-900 font-['Archivo']">
                    <CalendarDays className="w-5 h-5 text-blue-600" />
                    Selectează Data
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    locale={ro}
                    disabled={isDayDisabled}
                    fromDate={new Date()}
                    toDate={addDays(new Date(), 60)}
                    className="rounded-md border border-slate-200"
                    data-testid="booking-calendar"
                  />
                  {selectedDate && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-700">
                        <span className="font-semibold">Data selectată:</span>{" "}
                        {format(selectedDate, "EEEE, dd MMMM yyyy", { locale: ro })}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Time Slots */}
              {selectedDate && (
                <Card className="border border-slate-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-slate-900 font-['Archivo']">
                      <Clock className="w-5 h-5 text-blue-600" />
                      Selectează Ora
                    </CardTitle>
                    {slotsInfo && (
                      <p className="text-sm text-slate-500">
                        {slotsInfo.booked_count} / {slotsInfo.max_per_day} programări în această zi
                      </p>
                    )}
                  </CardHeader>
                  <CardContent>
                    {slotsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                        <span className="ml-2 text-slate-600">Se încarcă...</span>
                      </div>
                    ) : availableSlots.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {availableSlots.map((slot) => (
                          <Button
                            key={slot}
                            type="button"
                            variant={formData.preferred_time === slot ? "default" : "outline"}
                            className={`${
                              formData.preferred_time === slot 
                                ? "bg-blue-600 text-white" 
                                : "hover:bg-blue-50 hover:border-blue-300"
                            }`}
                            onClick={() => handleSelectChange("preferred_time", slot)}
                            data-testid={`time-slot-${slot}`}
                          >
                            {slot}
                          </Button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <XCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-600 font-medium">
                          {slotsInfo?.message || "Nu sunt locuri disponibile în această zi"}
                        </p>
                        <p className="text-sm text-slate-500 mt-1">
                          Te rugăm să selectezi altă dată
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-3"
            >
              <Card className="border border-slate-200">
                <CardHeader>
                  <CardTitle className="text-slate-900 font-['Archivo']">
                    Completează Detaliile
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Selected Date/Time Summary */}
                    {selectedDate && formData.preferred_time && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-green-800">
                            {format(selectedDate, "EEEE, dd MMMM yyyy", { locale: ro })} la ora {formData.preferred_time}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-700">
                        Nume complet *
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Ion Popescu"
                          className="pl-10 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                          data-testid="booking-name-input"
                          required
                        />
                      </div>
                    </div>

                    {/* Email & Phone */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-700">
                          Email *
                        </Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="email@exemplu.ro"
                            className="pl-10 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                            data-testid="booking-email-input"
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-slate-700">
                          Telefon *
                        </Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="0700 000 000"
                            className="pl-10 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                            data-testid="booking-phone-input"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Category */}
                    <div className="space-y-2">
                      <Label className="text-slate-700">
                        Categoria permis *
                      </Label>
                      <Select 
                        value={formData.license_category}
                        onValueChange={(value) => handleSelectChange("license_category", value)}
                      >
                        <SelectTrigger 
                          className="h-12 border-slate-200" 
                          data-testid="booking-category-select"
                        >
                          <div className="flex items-center gap-2">
                            <Car className="w-5 h-5 text-slate-400" />
                            <SelectValue placeholder="Selectează categoria" />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.code}>
                              Categoria {cat.code} - {cat.name.replace("Categoria ", "")}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Message */}
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-slate-700">
                        Mesaj (opțional)
                      </Label>
                      <div className="relative">
                        <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          placeholder="Ai întrebări sau cerințe speciale?"
                          className="pl-10 min-h-[100px] border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                          data-testid="booking-message-input"
                        />
                      </div>
                    </div>

                    {/* Submit */}
                    <Button
                      type="submit"
                      disabled={loading || !selectedDate || !formData.preferred_time}
                      data-testid="booking-submit-btn"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold h-14 text-lg shadow-lg shadow-blue-600/20 disabled:opacity-50"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Se trimite...
                        </>
                      ) : (
                        <>
                          Trimite Programarea
                          <CheckCircle2 className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>

                    {(!selectedDate || !formData.preferred_time) && (
                      <p className="text-sm text-amber-600 text-center flex items-center justify-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        {!selectedDate ? "Selectează o dată din calendar" : "Selectează o oră disponibilă"}
                      </p>
                    )}

                    <p className="text-sm text-slate-500 text-center">
                      * Câmpuri obligatorii. Te vom contacta pentru confirmarea programării.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              { icon: CalendarDays, title: "Program Flexibil", desc: bookingSettings ? `${bookingSettings.working_hours?.start} - ${bookingSettings.working_hours?.end}` : "Luni - Vineri" },
              { icon: Clock, title: "Răspuns Rapid", desc: "Te contactăm în maxim 24h" },
              { icon: CheckCircle2, title: "Confirmare", desc: "Primești confirmare pe email" }
            ].map((item, idx) => (
              <div key={idx} className="flex flex-col items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-slate-900">{item.title}</h3>
                <p className="text-slate-600 text-sm mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
